<template>
  <div>
    <v-card outlined rounded="lg" class="mb-3 pa-4">
    <div class="orbitron-title mb-3">Actividad</div><br>

    <v-tabs v-model="tab" grow class="custom-tabs">
      <v-tab>Movimientos</v-tab>
      <v-tab>Puntos diarios</v-tab>
      <v-tab>Premios</v-tab>
    </v-tabs>
  </v-card>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card
          v-for="e in events"
          :key="`ev-${e.id}`"
          class="mb-3"
          outlined
          rounded="lg"
        >
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div class="text-subtitle-1 font-weight-medium d-flex align-center">
                <span
                  v-if="e.tipo === 'clausulazo' || e.es_clausulazo"
                  class="material-symbols-outlined clausulazo-icon mr-1"
                >
                  bolt
                </span>

                <span>
                  {{ e.title }}

                  <span v-if="e.username" class="text-body-2 grey--text">
                    · {{ e.username }}
                  </span>
                </span>
              </div>

              <v-chip
                :color="eventChipColor(e)"
                small
                text-color="white"
              >
                {{ eventChipLabel(e) }}
              </v-chip>
            </div>

            <div class="text-caption grey--text mt-1">{{ e.timestamp }}</div>
            <div class="text-body-2 mt-2">{{ e.description }}</div>

            <div v-if="e.affectedSources && e.affectedSources.length" class="mt-3">
              <v-chip
                v-for="(s, i) in e.affectedSources"
                :key="i"
                class="mr-1 mb-1"
                small
                outlined
              >
                {{ s }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card
          v-for="day in dailyPoints"
          :key="day.date"
          class="mb-3"
          outlined
          rounded="lg"
        >
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div class="text-subtitle-1 font-weight-medium">
              Puntuación {{ formatDate(fechaPuntuacion(day.date)) }}
              <div class="text-caption grey--text">
                para previsión de {{ formatDate(day.date) }}
              </div>
            </div>
              <v-chip small color="primary" text-color="white">
                {{ formatSignedPoints(day.puntos_dia) }} pts
              </v-chip>
            </div>

            <div
              v-if="hasServicesAdjustment(day)"
              class="day-profit-breakdown mt-2"
            >
              <div class="profit-row">
                <span>Ganancia mercado diario:</span>

                <b
                  :class="
                    signedValueClass(
                      gananciaMercadoDiario(
                        day.ganancia_total,
                        day.servicios_ajuste_total
                      )
                    )
                  "
                >
                  {{
                    formatCurrency(
                      gananciaMercadoDiario(
                        day.ganancia_total,
                        day.servicios_ajuste_total
                      )
                    )
                  }}
                </b>
              </div>

              <div class="profit-row">
                <span>Ganancia servicios de ajuste:</span>

                <b :class="signedValueClass(day.servicios_ajuste_total)">
                  {{ formatSignedCurrency(day.servicios_ajuste_total) }}
                </b>
              </div>

              <div class="profit-row profit-row-total">
                <span>Total:</span>

                <b :class="signedValueClass(day.ganancia_total)">
                  {{ formatCurrency(day.ganancia_total) }}
                </b>
              </div>
            </div>

            <div v-else class="text-body-2 mt-2">
              Ganancia total:
              <b>{{ formatCurrency(day.ganancia_total) }}</b>
            </div>

            <div class="text-body-2">
              Puntos acumulados: <b>{{ day.puntos_totales }}</b>
            </div>
            <div class="text-body-2 mb-3">
              Posición: <b>#{{ day.posicion }}</b>
            </div>

            <div class="text-caption grey--text mb-2">Detalle</div>

            <v-list dense>
              <v-list-item
                v-for="(item, index) in day.details"
                :key="`${day.date}-${item.tipo || 'activo'}-${item.id || item.activo_id || item.nombre || index}-${index}`"
                class="px-0 activity-detail-item"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <span
                      v-if="item.tipo === 'minijuego'"
                      class="material-symbols-outlined mr-1 v-middle game-icon"
                    >
                      stadia_controller
                    </span>

                    {{ item.tipo === 'minijuego' ? nombreMinijuego(item.nombre) : item.nombre }}
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    <template v-if="item.tipo === 'minijuego'">
                      {{ descripcionMinijuego(item.descripcion) }}
                    </template>

                    <template v-else>
                      {{ item.tecnologia }} · {{ item.mw }} MW
                    </template>
                  </v-list-item-subtitle>

                  <div
                    v-if="item.tipo === 'activo' && item.servicios_ajuste"
                    class="adjustment-detail mt-2"
                  >
                    <span class="material-symbols-outlined adjustment-icon">
                      tune
                    </span>

                    <div class="adjustment-content">
                      <div class="adjustment-title mb-1">
                        Desglose de ganancias
                      </div>

                      <div class="adjustment-profit-row">
                        <span>Ganancia mercado diario:</span>

                        <b
                          :class="
                            signedValueClass(
                              gananciaMercadoDiario(
                                item.ganancia_activo,
                                item.servicios_ajuste.valor
                              )
                            )
                          "
                        >
                          {{
                            formatCurrency(
                              gananciaMercadoDiario(
                                item.ganancia_activo,
                                item.servicios_ajuste.valor
                              )
                            )
                          }}
                        </b>
                      </div>

                      <div class="adjustment-profit-row">
                        <span>Ganancia servicios de ajuste:</span>

                        <b :class="signedValueClass(item.servicios_ajuste.valor)">
                          {{ formatSignedCurrency(item.servicios_ajuste.valor) }}
                        </b>
                      </div>

                      <div class="adjustment-profit-row adjustment-profit-total">
                        <span>Total:</span>

                        <b :class="signedValueClass(item.ganancia_activo)">
                          {{ formatCurrency(item.ganancia_activo) }}
                        </b>
                      </div>
                    </div>

                  </div>
                </v-list-item-content>

                <v-list-item-action>
                  <div class="text-right">
                    <template v-if="item.tipo === 'minijuego'">
                      <div class="font-weight-medium money-action">
                        {{ formatSignedCurrency(item.ganancia_activo) }}
                      </div>
                    </template>

                    <template v-else>
                      <div class="font-weight-medium">
                        {{ formatSignedPoints(item.puntos_activo) }} pts
                      </div>

                      <div
                        v-if="!item.servicios_ajuste"
                        class="text-caption"
                        :class="signedValueClass(item.ganancia_activo)"
                      >
                        {{ formatSignedCurrency(item.ganancia_activo) }}
                      </div>
                    </template>
                  </div>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <div v-if="!dailyPoints || dailyPoints.length === 0" class="text-body-2 grey--text">
          Aún no hay puntos diarios calculados.
        </div>
      </v-tab-item>
              <v-tab-item>
          <v-card
            v-for="p in weeklyPrizes"
            :key="`prize-${p.id}`"
            class="mb-3"
            outlined
            rounded="lg"
          >
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-subtitle-1 font-weight-medium">
                    {{ p.username }}
                    <span v-if="p.is_current_user" class="text-caption grey--text">
                      · Tú
                    </span>
                  </div>

                  <div class="text-caption grey--text mt-1">
                    Semana del {{ formatDate(p.fecha_inicio) }} al {{ formatDate(p.fecha_fin) }}
                  </div>
                </div>

                <v-chip small color="green" text-color="white">
                  +{{ formatCurrency(p.dinero_total) }}
                </v-chip>
              </div>

              <div class="mt-3 text-body-2">
                Posición semanal: <b>#{{ p.posicion_semana }}</b>
              </div>

              <div class="text-body-2">
                Puntos de la semana: <b>{{ p.puntos_semana }}</b>
              </div>

              <v-divider class="my-3"></v-divider>

              <div class="d-flex justify-space-between text-body-2">
                <span>Bonus por rendimiento</span>
                <b>{{ formatCurrency(p.bonus_rendimiento) }}</b>
              </div>

              <div class="d-flex justify-space-between text-body-2 mt-1">
                <span>Ayuda compensatoria</span>
                <b>{{ formatCurrency(p.ayuda_compensatoria) }}</b>
              </div>

              <div v-if="p.descripcion" class="text-caption grey--text mt-3">
                {{ p.descripcion }}
              </div>

              <div class="text-caption grey--text mt-2">
                Liquidado: {{ p.created_at }}
              </div>
            </v-card-text>
          </v-card>

          <div v-if="!weeklyPrizes || weeklyPrizes.length === 0" class="text-body-2 grey--text">
            Aún no hay premios semanales liquidados.
          </div>
        </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
export default {
  name: "GameActivity",
  props: {
    events: { type: Array, required: true },
    dailyPoints: { type: Array, default: () => [] },
    weeklyPrizes: { type: Array, default: () => [] },
  },
  data() {
    return {
      tab: 0,
    };
  },
  methods: {
  gananciaMercadoDiario(gananciaTotal, serviciosAjuste) {
  const total = Number(gananciaTotal) || 0;
  const ajuste = Number(serviciosAjuste) || 0;

  return total - ajuste;
},
    eventChipColor(event) {
  if (event.tipo === "clausulazo" || event.es_clausulazo) {
    return "deep-orange";
  }

  return this.chipColor(event.impact);
},

eventChipLabel(event) {
  if (event.tipo === "clausulazo" || event.es_clausulazo) {
    return "CLAUSULAZO";
  }

  return this.impactLabel(event.impact);
},
  fechaPuntuacion(fechaPrevision) {
  if (!fechaPrevision) return "";

  const fecha = new Date(`${fechaPrevision}T12:00:00`);

  fecha.setDate(fecha.getDate() - 1);

  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, "0");
  const day = String(fecha.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
},
    slug(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
},

nombreMinijuego(nombre) {
  const original = String(nombre || "").trim();

  const sinPrefijo = original
    .replace(/^Ganado en minijuego\s*/i, "")
    .trim();

  const key = this.slug(sinPrefijo || original);

  const nombres = {
    quiz_market: "Quiz del mercado",
    quiz: "Quiz del mercado",
    apagon_masivo: "Apagón Masivo",
    foton_reboton: "Fotón Rebotón",
    electric_mad_market: "Electric Mad Market",
  };

  const nombreBonito = nombres[key] || sinPrefijo || original;

  if (/^Ganado en minijuego/i.test(original)) {
    return `Ganado en minijuego ${nombreBonito}`;
  }

  return nombreBonito;
},

descripcionMinijuego(descripcion) {
  const texto = String(descripcion || "").trim();

  if (!texto || texto.toLowerCase() === "recompensa arcade") {
    return "Recompensa de minijuego";
  }

  return texto;
},

hasServicesAdjustment(day) {
  return Array.isArray(day && day.details)
    && day.details.some(
      item => item && item.tipo === "activo" && item.servicios_ajuste
    );
},

signedValueClass(value) {
  const numero = Number(value) || 0;

  if (numero > 0) return "positive-value";
  if (numero < 0) return "negative-value";

  return "neutral-value";
},

formatNumber(value, maximumFractionDigits = 2) {
  const numero = Number(value) || 0;

  return new Intl.NumberFormat("es-ES", {
    maximumFractionDigits,
  }).format(numero);
},

formatPricePerMwh(value) {
  const numero = Number(value) || 0;

  return `${new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numero)} €/MWh`;
},

formatAdjustmentPercent(value) {
  const numero = Number(value) || 0;

  // Admite tanto ratios (0,15) como porcentajes ya expresados (15).
  const porcentaje = Math.abs(numero) <= 1
    ? numero * 100
    : numero;

  return `${new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(porcentaje)} %`;
},

    chipColor(impact) {
      if (impact === "positive") return "green";
      if (impact === "negative") return "red";
      return "grey";
    },
    impactLabel(impact) {
      if (impact === "positive") return "+";
      if (impact === "negative") return "-";
      return "·";
    },
    formatCurrency(value) {
      try {
        return new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
          useGrouping: "always",
        }).format(Number(value) || 0);
      } catch (e) {
        return `${value || 0}€`;
      }
    },
    formatSignedPoints(value) {
  const puntos = Number(value) || 0;
  return puntos > 0 ? `+${puntos}` : `${puntos}`;
},
    formatSignedCurrency(value) {
  const numero = Number(value) || 0;
  const importe = this.formatCurrency(numero);

  return numero > 0 ? `+${importe}` : importe;
},
    formatDate(value) {
      try {
        return new Intl.DateTimeFormat("es-ES").format(new Date(value));
      } catch (e) {
        return value;
      }
    },
  },
};
</script>
<style scoped>
.orbitron-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 20px;
  font-weight: 600;
}
.custom-tabs :deep(.v-tab) {
  color: #a7a4a4 !important;        /* color tab inactiva */

}

.custom-tabs :deep(.v-tab--active) {
  color: #414141 !important;        /* color tab activa */
  background-color: #f3e6b0;
}

.custom-tabs :deep(.v-tabs-slider) {
  background-color: #f3cd33 !important; /* línea inferior activa */
}
.v-middle {
  vertical-align: middle;
}

.game-icon {
  font-size: 18px;
  color: #31635f;
}
.money-action {
  color: #2e7d32;
}

.activity-detail-item {
  align-items: flex-start;
}

.adjustment-day-summary {
  margin-top: 3px;
  margin-bottom: 3px;
}

.adjustment-detail {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 7px 9px;
  border: 1px solid rgba(49, 99, 95, 0.22);
  border-radius: 9px;
  background: rgba(49, 99, 95, 0.07);
  white-space: normal;
}

.adjustment-icon {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 18px;
  color: #31635f;
}

.adjustment-content {
  min-width: 0;
}

.adjustment-title {
  color: #31635f;
  font-size: 12px;
  font-weight: 700;
}

.adjustment-meta {
  color: #526966;
  white-space: normal;
}

.positive-value {
  color: #2e7d32 !important;
}

.negative-value {
  color: #c62828 !important;
}

.neutral-value {
  color: #616161 !important;
}
.clausulazo-icon {
  color: #f4511e;
  font-size: 22px;
}
.day-profit-breakdown {
  max-width: 430px;
  margin-bottom: 4px;
}

.profit-row,
.adjustment-profit-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.profit-row {
  font-size: 14px;
  margin-bottom: 3px;
}

.profit-row-total {
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px solid rgba(49, 99, 95, 0.2);
}

.adjustment-content {
  flex: 1;
  min-width: 0;
}

.adjustment-profit-row {
  font-size: 12px;
  line-height: 1.5;
}

.adjustment-profit-row span {
  color: #526966;
}

.adjustment-profit-total {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(49, 99, 95, 0.2);
}

.adjustment-profit-total span,
.adjustment-profit-total b {
  font-weight: 700;
}

@media (max-width: 600px) {
  .profit-row,
  .adjustment-profit-row {
    align-items: flex-start;
    gap: 8px;
  }

  .profit-row b,
  .adjustment-profit-row b {
    flex: 0 0 auto;
    text-align: right;
  }
}
</style>
